import { useState, useEffect } from 'react';
import { Dialog, DialogPanel, Title, Text, Button, Flex, Badge } from '@tremor/react';
import { api } from '../../lib/api';
import type { BulkRetryResponse } from '../../types';

const QUEUE_LABELS: Record<string, string> = {
  fetch: 'Fetch',
  extract: 'Extract',
  evaluate: 'Evaluate',
  annotate: 'Annotate',
  tag: 'Tag',
};

interface FailedJobsModalProps {
  isOpen: boolean;
  onClose: () => void;
  queueName?: string | null;
}

export function FailedJobsModal({ isOpen, onClose, queueName }: FailedJobsModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isDryRun, setIsDryRun] = useState(false);
  const [result, setResult] = useState<BulkRetryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchFailed = async (dryRun: boolean) => {
    setIsLoading(true);
    setIsDryRun(dryRun);
    setError(null);
    if (dryRun) setResult(null);

    try {
      const response = queueName
        ? await api.retryQueueFailed(queueName, { dryRun, limit: 100 })
        : await api.bulkRetry({ status: 'failed', dryRun, limit: 100 });
      setResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch failed jobs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    setError(null);
    setIsDryRun(false);
    onClose();
  };

  // Load failed jobs on open
  useEffect(() => {
    if (isOpen && !result) {
      fetchFailed(true);
    }
  }, [isOpen]);

  const title = queueName
    ? `Failed Jobs — ${QUEUE_LABELS[queueName] || queueName}`
    : 'Failed Jobs';

  return (
    <Dialog open={isOpen} onClose={handleClose} static={true}>
      <DialogPanel className="bg-gray-900 border border-gray-800 max-w-lg">
        <Flex justifyContent="between" alignItems="center">
          <Title className="text-gray-100">{title}</Title>
          <Button size="xs" variant="secondary" onClick={() => fetchFailed(true)} loading={isLoading && isDryRun}>
            Refresh
          </Button>
        </Flex>

        {error && (
          <div className="mt-4 bg-red-950/30 border border-red-900/50 rounded p-2">
            <Text className="text-red-400 text-sm">{error}</Text>
          </div>
        )}

        {result && (
          <div className="mt-4 space-y-4">
            {/* Summary */}
            <div className="bg-gray-800/50 rounded p-3">
              <Flex justifyContent="between" alignItems="center">
                <Text className="text-gray-400">Found</Text>
                <Badge color={result.found > 0 ? 'red' : 'emerald'} size="sm">
                  {result.found} failed job{result.found !== 1 ? 's' : ''}
                </Badge>
              </Flex>
            </div>

            {/* Document List (dry run) */}
            {isDryRun && result.documents && result.documents.length > 0 && (
              <div>
                <Text className="text-gray-500 text-xs mb-2">
                  Documents to retry (showing up to 100)
                </Text>
                <div className="bg-gray-800/50 rounded max-h-60 overflow-y-auto">
                  {result.documents.map((docId) => (
                    <div
                      key={docId}
                      className="px-3 py-2 border-b border-gray-700 last:border-b-0"
                    >
                      <Text className="text-gray-300 font-mono text-sm">{docId}</Text>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Results (after retry) */}
            {!isDryRun && result.successful !== undefined && (
              <div className="space-y-2">
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Successfully retried</Text>
                  <Text className="text-emerald-400">{result.successful}</Text>
                </Flex>
                {result.failed > 0 && (
                  <Flex justifyContent="between">
                    <Text className="text-gray-500">Failed to retry</Text>
                    <Text className="text-red-400">{result.failed}</Text>
                  </Flex>
                )}
              </div>
            )}

            {/* No failed jobs */}
            {result.found === 0 && (
              <div className="bg-emerald-950/30 border border-emerald-900/50 rounded p-3 text-center">
                <Text className="text-emerald-400">No failed jobs found</Text>
              </div>
            )}
          </div>
        )}

        <Flex justifyContent="end" className="mt-6 gap-3">
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          {result && isDryRun && result.found > 0 && (
            <Button onClick={() => fetchFailed(false)} loading={isLoading && !isDryRun} color="red">
              Retry All ({result.found})
            </Button>
          )}
        </Flex>
      </DialogPanel>
    </Dialog>
  );
}
